import { createContext, useMemo, useState } from 'react';
import { defaultContextValue } from '../constant';
import { trackOpenArticle } from '../tracks';
import { HelpAndDocumentationContextValue } from '../types';
import useHelpAndDocumentationData from './useHelpAndDocumentationData';
import { useHelpAndDocumentationSearch } from './useHelpAndDocumentationSearch';

export const HelpAndDocumentationContext = createContext(defaultContextValue);

export const useHelpAndDocumentationContext = () => {
  const [indexCategory, setIndexCategory] = useState(0);
  const [indexSubCategory, setIndexSubCategory] = useState(0);
  const [indexArticle, setIndexArticle] = useState(0);
  const [prefixSubCategory, setPrefixSubCategory] = useState<string[]>([]);
  const [isArticleFromCategory, setIsArticleFromCategory] = useState(false);
  const [isSideModalCategoryShowed, setIsSideModalCategoryShowed] =
    useState(false);
  const [isSideModalArticleShowed, setIsSideModalArticleShowed] =
    useState(false);
  const { data, findIndexesByTitle } = useHelpAndDocumentationData();
  const { searchArticles, searchQuery, results, getSuggestion, isSearching } =
    useHelpAndDocumentationSearch();

  const selectedCategory = useMemo(
    () => data[indexCategory],
    [data, indexCategory],
  );

  const selectedArticle = useMemo(
    () =>
      data[indexCategory].sub_category[indexSubCategory].articles[indexArticle],
    [indexCategory, indexSubCategory, indexArticle, data],
  );

  const onClickCategory = (index: number, subCategoryPrefix?: string[]) => {
    setIndexCategory(index);
    setIndexSubCategory(0);
    setIndexArticle(0);
    setIsSideModalCategoryShowed(true);
    setPrefixSubCategory(subCategoryPrefix ?? []);
  };

  const onClickArticle = (
    category: number,
    subCategory: number,
    article: number,
    title: string,
  ) => {
    setIndexArticle(article);
    setIndexCategory(category);
    setIndexSubCategory(subCategory);
    if (isSideModalCategoryShowed) {
      setIsArticleFromCategory(true);
      setIsSideModalCategoryShowed(false);
    } else {
      setIsArticleFromCategory(false);
    }
    setIsSideModalArticleShowed(true);
    trackOpenArticle(title, data[category].category);
  };

  const onCloseCategorySideModal = () => {
    setIsSideModalCategoryShowed(false);
    setPrefixSubCategory([]);
  };

  const onCloseArticleSideModal = () => {
    setIsSideModalArticleShowed(false);
    if (isArticleFromCategory) {
      setIsSideModalCategoryShowed(true);
    }
  };

  const onSuggestionClick = (searchQuery: string) => {
    const indexes = findIndexesByTitle(searchQuery);

    if (indexes)
      onClickArticle(
        indexes.categoryIndex,
        indexes?.subCategoryIndex,
        indexes?.articleIndex,
        searchQuery,
      );
    else {
      searchArticles(searchQuery);
    }
  };

  const context: HelpAndDocumentationContextValue = {
    data,
    results,
    getSuggestion,
    searchQuery,
    selectedCategory,
    selectedArticle,
    isSearching,
    isSideModalArticleShowed,
    isSideModalCategoryShowed,
    indexCategory,
    onClickCategory,
    onClickArticle,
    onCloseArticleSideModal,
    onCloseCategorySideModal,
    onSuggestionClick,
    prefixSubCategory,
  };

  return { context };
};
